import React, { useState, Fragment } from 'react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { VoiceAgentCall } from '../../types';
import { ChevronDown, ChevronUp, Clock, MessageSquare } from 'lucide-react';
interface CallHistoryPanelProps {
  calls: VoiceAgentCall[];
}
export function CallHistoryPanel({
  calls
}: CallHistoryPanelProps) {
  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const getStatusBadge = (status: VoiceAgentCall['status']) => {
    switch (status) {
      case 'pending':
        return <Badge variant="info" data-id="element-49">Ausstehend</Badge>;
      case 'in_progress':
        return <Badge variant="warning" data-id="element-50">Läuft</Badge>;
      case 'completed':
        return <Badge variant="success" data-id="element-51">Abgeschlossen</Badge>;
      case 'failed':
        return <Badge variant="error" data-id="element-52">Fehlgeschlagen</Badge>;
    }
  };
  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')} Min`;
  };
  if (calls.length === 0) {
    return <Card data-id="element-53">
        <div className="text-center py-12" data-id="element-54">
          <MessageSquare className="h-12 w-12 text-slate-300 mx-auto mb-4" data-id="element-55" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-56">
            Noch keine Anrufe durchgeführt
          </h3>
          <p className="text-sm text-slate-500" data-id="element-57">
            Starten Sie Ihren ersten AI Voice Agent Anruf über die Kundenliste.
          </p>
        </div>
      </Card>;
  }
  return <Card data-id="element-58">
      <div className="overflow-x-auto" data-id="element-59">
        <table className="w-full text-sm text-left" data-id="element-60">
          <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200" data-id="element-61">
            <tr data-id="element-62">
              <th className="px-4 py-3 font-medium" data-id="element-63">Kunde</th>
              <th className="px-4 py-3 font-medium" data-id="element-64">Prompt</th>
              <th className="px-4 py-3 font-medium" data-id="element-65">Status</th>
              <th className="px-4 py-3 font-medium" data-id="element-66">Datum</th>
              <th className="px-4 py-3 font-medium" data-id="element-67">Dauer</th>
              <th className="px-4 py-3 font-medium text-right" data-id="element-68">Aktionen</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-69">
            {calls.map(call => <Fragment key={call.id} data-id="element-70">
                <tr className="hover:bg-slate-50 transition-colors" data-id="element-71">
                  <td className="px-4 py-3" data-id="element-72">
                    <div className="font-medium text-slate-900" data-id="element-73">
                      {call.clientName}
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-74">
                      {call.clientPhone}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-75">
                    {call.promptName}
                  </td>
                  <td className="px-4 py-3" data-id="element-76">{getStatusBadge(call.status)}</td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-77">
                    <div className="flex items-center gap-1" data-id="element-78">
                      <Clock className="h-3 w-3" data-id="element-79" />
                      {call.createdAt}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600" data-id="element-80">
                    {formatDuration(call.duration)}
                  </td>
                  <td className="px-4 py-3 text-right" data-id="element-81">
                    {call.transcript && <Button variant="ghost" size="sm" onClick={() => setExpandedCallId(expandedCallId === call.id ? null : call.id)} rightIcon={expandedCallId === call.id ? <ChevronUp className="h-4 w-4" data-id="element-83" /> : <ChevronDown className="h-4 w-4" data-id="element-84" />} data-id="element-82">
                        {expandedCallId === call.id ? 'Weniger' : 'Details'}
                      </Button>}
                  </td>
                </tr>

                {/* Expanded Transcript */}
                {expandedCallId === call.id && call.transcript && <tr data-id="element-85">
                    <td colSpan={6} className="px-4 py-4 bg-slate-50" data-id="element-86">
                      <div className="space-y-3" data-id="element-87">
                        <div data-id="element-88">
                          <h4 className="text-sm font-semibold text-slate-700 mb-2" data-id="element-89">
                            Gesprächsverlauf
                          </h4>
                          <div className="bg-white p-3 rounded-lg border border-slate-200 text-sm text-slate-700 leading-relaxed whitespace-pre-wrap" data-id="element-90">
                            {call.transcript}
                          </div>
                        </div>
                        {call.notes && <div data-id="element-91">
                            <h4 className="text-sm font-semibold text-slate-700 mb-2" data-id="element-92">
                              Notizen
                            </h4>
                            <div className="bg-white p-3 rounded-lg border border-slate-200 text-sm text-slate-700" data-id="element-93">
                              {call.notes}
                            </div>
                          </div>}
                      </div>
                    </td>
                  </tr>}
              </Fragment>)}
          </tbody>
        </table>
      </div>
    </Card>;
}